import { useQueryClient } from '@tanstack/react-query'
import { Languages, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router'

import { type CodigoDeIdioma, NOMBRE_DE_IDIOMA, type PeticionDeDiseno, useComponentesEnEspanol, useIdiomas } from '../api/idiomas'
import { ErrorDeLaApi, pedir } from '../api/nucleo'
import { Aviso, Boton, Casilla, Esqueleto, Selector, Tarjeta, Titulo } from '../componentes/ui'
import type { Contexto } from './Modulo'

/**
 * «Idiomas» de una web (CLAUDE.md §37): además del español, el inglés y el portugués. Encender uno no
 * traduce nada: abre `/en/…` (o `/pt/…`) para las versiones que el cliente escriba, y en cada página,
 * entrada o producto aparece «Crear versión en inglés». Apagarlo no borra nada: esas versiones dejan de verse.
 */
export const Idiomas = () => {
  const { espacio, sitio } = useOutletContext<Contexto>()
  const navegar = useNavigate()
  const clienteDeConsultas = useQueryClient()
  const { data, error, isPending } = useIdiomas(sitio?.id ?? null)
  // Los componentes que se quedarían en español: solo importa si la web tiene (o va a tener) otro idioma.
  const { data: enEspanol } = useComponentesEnEspanol(sitio?.id ?? null, Boolean(data && data.encendidos.length > 1))
  const [elegidos, setElegidos] = useState<CodigoDeIdioma[] | null>(null)
  const [existencias, setExistencias] = useState<CodigoDeIdioma | null>(null)
  const [guardando, setGuardando] = useState(false)
  const [aviso, setAviso] = useState<null | { tono: 'error' | 'ok'; texto: string }>(null)

  if (!sitio) return <Aviso tono="error">Esa web no existe, o no es tuya.</Aviso>
  if (isPending) return <Esqueleto />
  if (error || !data) return <Aviso tono="error">No se han podido leer los idiomas de la web. Recarga la página.</Aviso>

  const actuales = elegidos ?? data.encendidos
  // El idioma que lleva las existencias tiene que estar encendido: si se apaga, vuelve al principal.
  const elegidoParaExistencias = existencias ?? data.existencias?.idioma ?? data.principal
  const existenciasEn: CodigoDeIdioma | null = data.existencias ? (actuales.includes(elegidoParaExistencias) ? elegidoParaExistencias : data.principal) : null
  const cambiado = (elegidos !== null && [...elegidos].sort().join() !== [...data.encendidos].sort().join()) || (data.existencias !== null && existenciasEn !== data.existencias.idioma)

  const guardar = async () => {
    setGuardando(true)
    setAviso(null)
    try {
      await pedir('/panel/idiomas', { metodo: 'POST', cuerpo: { sitio: sitio.id, idiomas: actuales.filter((i) => i !== data.principal), ...(existenciasEn ? { idiomaDeExistencias: existenciasEn } : {}) } })
      await clienteDeConsultas.invalidateQueries({ queryKey: ['idiomas', sitio.id] })
      setElegidos(null)
      setExistencias(null)
      setAviso({ tono: 'ok', texto: 'Guardado. Los cambios se ven en tu web en un minuto.' })
    } catch (e) {
      setAviso({ tono: 'error', texto: e instanceof ErrorDeLaApi ? e.message : 'No se ha podido guardar. Vuelve a intentarlo.' })
    } finally {
      setGuardando(false)
    }
  }

  return (
    <>
      <Titulo descripcion="Tu web en otros idiomas, cada uno con sus propias páginas y su propio SEO." rotulo={sitio.nombre}>
        Idiomas
      </Titulo>
      <div className="flex max-w-2xl flex-col gap-4">
        <Aviso tono="info">
          Aquí no se traduce nada automáticamente. Al encender un idioma, cada página, entrada, producto, servicio, categoría o formulario tiene un botón «Crear versión en…» que copia el
          original en borrador para que lo escribas en ese idioma. La versión en inglés vive en <code>/en/…</code> y la portuguesa en <code>/pt/…</code>; los buscadores saben que son la
          misma página en otro idioma. Lo que es de toda la web —los ajustes, las zonas de envío, los tipos de contenido, los grupos de campos, los recursos de la agenda y el texto
          alternativo de cada foto— se traduce en su apartado «Otros idiomas» o «Traducciones»; lo que no traduzcas sale en español.
        </Aviso>
        <Tarjeta>
          <ul className="flex flex-col divide-y divide-linea">
            {data.disponibles.map((idioma) => {
              const principal = idioma.codigo === data.principal
              // Lo que el plan no trae no se enciende; lo que ya estaba encendido se deja apagar (§37).
              const fueraDelPlan = !principal && data.delPlan !== undefined && !(data.delPlan as string[]).includes(idioma.codigo) && !data.encendidos.includes(idioma.codigo)
              return (
                <li className="flex items-center gap-3 py-3" key={idioma.codigo}>
                  <Casilla
                    disabled={principal || fueraDelPlan || !data.puedeCambiar || guardando}
                    etiqueta={idioma.nombre}
                    id={`idioma-${idioma.codigo}`}
                    marcada={actuales.includes(idioma.codigo)}
                    onChange={(e) => setElegidos(e.target.checked ? [...actuales, idioma.codigo] : actuales.filter((i) => i !== idioma.codigo))}
                  />
                  <label className="flex flex-1 flex-col" htmlFor={`idioma-${idioma.codigo}`}>
                    <span className="font-medium">
                      {idioma.nombre} <span className="text-tenue">· {idioma.propio}</span>
                    </span>
                    <span className="text-sm text-tenue">
                      {principal ? (
                        'El idioma principal de tu web: va sin prefijo y no se apaga.'
                      ) : fueraDelPlan ? (
                        <>
                          Tu plan no lo incluye.{' '}
                          <Link className="text-violeta underline-offset-2 hover:underline" to={`/e/${espacio}/creditos`}>
                            Cambia de plan
                          </Link>{' '}
                          para tener la web en este idioma.
                        </>
                      ) : (
                        `Sus páginas viven en /${idioma.codigo}/…`
                      )}
                    </span>
                  </label>
                </li>
              )
            })}
          </ul>
        </Tarjeta>
        {data.existencias && actuales.length > 1 && (
          <Tarjeta>
            <div className="flex flex-col gap-2">
              <label className="font-medium" htmlFor="idioma-de-existencias">
                Existencias de la tienda
              </label>
              <p className="text-sm text-tenue">
                Cada producto lleva sus existencias en una sola de sus versiones: las demás usan esas, y una venta en cualquier idioma las descuenta. Las cambias en la versión de
                este idioma.
              </p>
              <Selector disabled={!data.puedeCambiar || guardando} id="idioma-de-existencias" onChange={(e) => setExistencias(e.target.value as CodigoDeIdioma)} value={existenciasEn ?? data.principal}>
                {actuales.map((codigo) => (
                  <option key={codigo} value={codigo}>
                    La versión en {NOMBRE_DE_IDIOMA[codigo].toLowerCase()}
                  </option>
                ))}
              </Selector>
              {existenciasEn !== data.existencias.idioma && (
                <p className="text-sm text-tenue">
                  Al cambiarlo, mandan las existencias que ya tenga la versión en {NOMBRE_DE_IDIOMA[existenciasEn ?? data.principal].toLowerCase()}: revísalas antes de guardar. Un producto
                  sin versión en ese idioma sigue con las suyas.
                </p>
              )}
            </div>
          </Tarjeta>
        )}
        {enEspanol && enEspanol.componentes.length > 0 && (
          <Aviso tono="aviso">
            <div className="flex flex-col gap-2">
              <p>
                {enEspanol.componentes.length === 1 ? 'Un componente de tu diseño tiene' : `${enEspanol.componentes.length} componentes de tu diseño tienen`} textos escritos en español: en
                las otras versiones de tu web saldrán en español hasta que se cambien.
              </p>
              <ul className="list-disc pl-5 text-sm">
                {enEspanol.componentes.map((c) => (
                  <li key={c.ruta}>
                    <code>{c.ruta}</code>: {c.motivo}
                    {c.ejemplos.length > 0 && <span className="text-tenue"> Por ejemplo: {c.ejemplos.map((e) => `«${e}»`).join(', ')}.</span>}
                  </li>
                ))}
              </ul>
              {enEspanol.arreglo ? (
                <div>
                  <Boton
                    icono={<Sparkles />}
                    onClick={() => {
                      const peticion: PeticionDeDiseno = { alcance: 'sitio', rutas: enEspanol.arreglo!.rutas, prompt: enEspanol.arreglo!.prompt }
                      void navegar(`/e/${espacio}/s/${sitio.id}/diseno`, { state: { peticion } })
                    }}
                    tipo="secundario"
                  >
                    Arreglarlo con Diseño con IA
                  </Boton>
                  <p className="mt-1 text-sm text-tenue">
                    Se abre Diseño con la petición ya escrita; verás lo que cuesta antes de generar.
                    {enEspanol.arreglo.quedan > 0 ? ` Van ${enEspanol.arreglo.rutas.length} cada vez: después quedarán ${enEspanol.arreglo.quedan}.` : ''}
                  </p>
                </div>
              ) : (
                <p className="text-sm">Pídeselo a quien diseña tu web, o escríbenos y lo hacemos nosotros.</p>
              )}
            </div>
          </Aviso>
        )}
        {!data.puedeCambiar && <Aviso tono="info">Los idiomas de la web los cambia quien administra la cuenta.</Aviso>}
        {aviso && <Aviso tono={aviso.tono}>{aviso.texto}</Aviso>}
        {data.puedeCambiar && (
          <div>
            <Boton cargando={guardando} disabled={!cambiado} icono={<Languages />} onClick={() => void guardar()}>
              Guardar
            </Boton>
          </div>
        )}
        {elegidos && data.encendidos.some((i) => !elegidos.includes(i)) && (
          <Aviso tono="aviso">Al apagar un idioma, sus versiones no se borran: dejan de verse en tu web hasta que lo vuelvas a encender.</Aviso>
        )}
      </div>
    </>
  )
}
